/**
 * BillingIntervalToggle Component
 *
 * Segmented control for selecting monthly or yearly billing interval.
 */

import { SegmentedControl } from '@components/primitives/SegmentedControl';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import type { BillingInterval } from '../types';

interface BillingIntervalToggleProps {
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  discountRange?: { min: number; max: number } | null;
}

export function BillingIntervalToggle({
  value,
  onChange,
  disabled,
  size = 'medium',
  discountRange,
}: BillingIntervalToggleProps) {
  const { subscription } = useEventTracking();

  // Calculate discount label - null if no discount data
  const discountLabel = discountRange
    ? discountRange.min === discountRange.max
      ? `-${discountRange.min}%`
      : `-${discountRange.min}-${discountRange.max}%`
    : null;

  const options = [
    { value: 'month', label: 'Monthly' },
    {
      value: 'year',
      label: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <span>Annual</span>
          {discountLabel && (
            <Box
              component="span"
              sx={theme => ({
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                borderRadius: '10px',
                px: 0.75,
                py: 0.25,
                fontSize: '0.65rem',
                fontWeight: 700,
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '20px',
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.2),
              })}
            >
              {discountLabel}
            </Box>
          )}
        </Box>
      ),
    },
  ];

  const handleChange = (newValue: string) => {
    if (newValue === 'month' || newValue === 'year') {
      // Track billing interval toggle interaction (preview only, no mutation here)
      subscription.billingIntervalToggled({
        from_interval: value,
        to_interval: newValue,
      });

      onChange(newValue);
    }
  };

  return (
    <SegmentedControl
      options={options}
      value={value}
      onChange={handleChange}
      disabled={disabled}
      size={size}
      ariaLabel="Billing interval"
    />
  );
}
